<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { X } from 'lucide-vue-next'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import { Textarea } from '@/components/ui/textarea'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables'
  import { DialogClose } from 'radix-vue'
  import type { DeactivationReason } from './useAiAssistantBeta'

  const open = defineModel<boolean>('open', { default: false })

  const emit = defineEmits<{
    confirm: [reason: DeactivationReason, feedback: string]
  }>()

  const { t } = useTranslation({
    en: {
      title: 'Deactivate AI assistant beta?',
      bodyPart1: 'Are you sure you want to',
      bodyFully: 'fully',
      bodyPart2: 'deactivate AI assistant beta for',
      bodyAllUsers: 'all users',
      bodyPart3: 'in your law firm? You can deactivate individual features separately.',
      reasonLabel: 'If you want to proceed with deactivation, please select the reason:',
      reasonDoesntFit: "AI assistant doesn't fit my workflow",
      reasonInaccurate: 'AI suggestions are not accurate',
      reasonOther: 'Other (please specify)',
      feedbackLabel: 'Additional feedback (optional)',
      feedbackPlaceholder: '',
      cancel: 'Cancel',
      deactivate: 'Deactivate',
      close: 'Close'
    },
    de: {
      title: 'AI Assistant Beta deaktivieren?',
      bodyPart1: 'Möchtest du AI Assistant Beta wirklich',
      bodyFully: 'vollständig',
      bodyPart2: 'für',
      bodyAllUsers: 'alle Nutzer',
      bodyPart3:
        'in deiner Kanzlei deaktivieren? Du kannst einzelne Funktionen separat deaktivieren.',
      reasonLabel: 'Wenn du fortfahren möchtest, wähle bitte den Grund aus:',
      reasonDoesntFit: 'AI Assistant passt nicht zu meinem Workflow',
      reasonInaccurate: 'KI-Vorschläge sind nicht genau',
      reasonOther: 'Sonstiges (bitte angeben)',
      feedbackLabel: 'Zusätzliches Feedback (optional)',
      feedbackPlaceholder: '',
      cancel: 'Abbrechen',
      deactivate: 'Deaktivieren',
      close: 'Schließen'
    }
  })

  const reason = ref<DeactivationReason | undefined>(undefined)
  const feedback = ref('')

  const canConfirm = computed(() => reason.value !== undefined)

  // Reset form whenever the dialog opens fresh.
  watch(open, (isOpen) => {
    if (isOpen) {
      reason.value = undefined
      feedback.value = ''
    }
  })

  function handleConfirm() {
    if (!reason.value) return
    emit('confirm', reason.value, feedback.value.trim())
    open.value = false
  }

  const radioItemClass =
    'h-4 w-4 border-[#D1D5DB] bg-white text-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] data-[state=checked]:border-[#9333EA] data-[state=checked]:bg-[#9333EA]'

  const bodyBoldClass = 'text-base font-semibold leading-6 text-[#1F2937]'
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent
      :class="[
        'flex w-[609px] max-w-[609px] flex-col gap-4 rounded-xl border border-[#D1D5DB] bg-white p-6',
        'shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] sm:max-w-[609px] sm:rounded-xl',
        '[&_[data-cy=dialogCloseBtn]]:hidden'
      ]"
    >
      <div class="flex flex-col items-start gap-4 self-stretch">
        <div class="flex items-center justify-between self-stretch">
          <h2 class="text-xl font-semibold leading-8 text-[#1F2937]">{{ t('title') }}</h2>
          <DialogClose
            :aria-label="t('close')"
            class="flex h-6 w-6 items-center justify-center rounded-sm text-[#1F2937] opacity-70 hover:opacity-100
              focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <X
              class="h-6 w-6"
              :stroke-width="1.5"
            />
          </DialogClose>
        </div>

        <p class="self-stretch text-base font-normal leading-6 text-[#1F2937]">
          {{ t('bodyPart1') }}
          <span :class="bodyBoldClass">{{ t('bodyFully') }}</span>
          {{ t('bodyPart2') }}
          <span :class="bodyBoldClass">{{ t('bodyAllUsers') }}</span>
          {{ t('bodyPart3') }}
        </p>

        <div class="flex flex-col items-start gap-2">
          <p class="self-stretch text-base font-medium leading-6 text-[#1F2937]">
            {{ t('reasonLabel') }}
          </p>
          <RadioGroup
            v-model="reason"
            class="flex flex-col gap-2 self-stretch"
          >
            <label class="inline-flex cursor-pointer items-center gap-2 self-stretch">
              <RadioGroupItem
                value="doesnt_fit_workflow"
                :class="radioItemClass"
              />
              <span class="text-base font-normal leading-6 text-[#1F2937]">
                {{ t('reasonDoesntFit') }}
              </span>
            </label>
            <label class="inline-flex cursor-pointer items-center gap-2 self-stretch">
              <RadioGroupItem
                value="inaccurate_suggestions"
                :class="radioItemClass"
              />
              <span class="text-base font-normal leading-6 text-[#1F2937]">
                {{ t('reasonInaccurate') }}
              </span>
            </label>
            <label class="inline-flex cursor-pointer items-center gap-2 self-stretch">
              <RadioGroupItem
                value="other"
                :class="radioItemClass"
              />
              <span class="text-base font-normal leading-6 text-[#1F2937]">
                {{ t('reasonOther') }}
              </span>
            </label>
          </RadioGroup>
        </div>

        <div class="flex flex-col items-start gap-1 self-stretch">
          <label
            for="ai-beta-deactivate-feedback"
            class="text-base font-medium leading-6 text-[#1F2937]"
          >
            {{ t('feedbackLabel') }}
          </label>
          <Textarea
            id="ai-beta-deactivate-feedback"
            v-model="feedback"
            :placeholder="t('feedbackPlaceholder')"
            class="h-28 rounded-lg border-[#E5E7EB] px-2.5 py-2 text-base font-normal leading-6 text-[#1F2937]
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
          />
        </div>
      </div>

      <div class="flex items-start justify-end gap-2 self-stretch">
        <DialogClose as-child>
          <Button
            variant="secondary"
            class="h-10 rounded-lg border-[#E5E7EB] px-4 text-base font-medium leading-6 text-[#1F2937]
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
          >
            {{ t('cancel') }}
          </Button>
        </DialogClose>
        <Button
          variant="destructive"
          :disabled="!canConfirm"
          class="h-10 rounded-lg bg-[#DC2626] px-4 text-base font-medium leading-6 text-white
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] hover:bg-[#B91C1C]"
          @click="handleConfirm"
        >
          {{ t('deactivate') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
